import React, { Fragment, useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQuery } from 'react-apollo';
import classNames from 'classnames';
import omit from 'lodash.omit';
import { useDispatch } from 'react-redux';
import pluralize from 'pluralize';

import withModal from '../../../../../components/global/modal/withModal';
import DismissButton from '../../../../../components/global/DismissButton';
import {
  externalShippingAddressCreate as externalShippingAddressCreateMutation,
  externalShippingAddressUpdate as externalShippingAddressUpdateMutation,
  externalShippingAddressDelete as externalShippingAddressDeleteMutation,
} from '../../../../../graphql/mutations/externalShippingAddress';
import { getErrorString } from '../../../../../util/normalize-errors';
import Alert from '../../../../../components/global/Alert';
import getUserForAccountCardConfigurationQuery from '../../../graphql/queries/getUserForAccountCardConfiguration';
import AddressListItem from '../AddressListItem';
import AddressListItemAddButton from '../AddressListItem/AddressListItemAddButton';
import Button from '../../../../../components/global/Button';
import AddressListItemImportButton from '../AddressListItem/AddressListItemImportButton';
import { openAlert, openModal } from '../../../../../actions/modal-actions';
import { CONTACT_SPREADSHEET_IMPORT_MODAL } from '../../ContactSpreadsheetImport/modal';
import ExternalShippingAddressForm from '../../../../../components/global/ExternalShippingAddressForm';
import CustomControl from '../../../../../components/global/CustomControl';
import useIsNativeWebView from '../../../../native-bridge/hooks/useIsWebView';
import AddressListItemContactsImportButton from '../AddressListItem/AddressListItemContactsImportButton';
import useBulkExternalShippingAddressImport from '../useBulkExternalShippingAddressImport';
import NativeActivityIndicator from '../../../../../components/global/NativeActivityIndicator';
import { canSendToAddress } from '../helpers';
import useVirtualPageView from '../../../../../apps/pro/services/analytics/useVirtualPageView';

const propTypes = {
  onClose: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  addresses: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string,
      address_line_1: PropTypes.string,
      address_line_2: PropTypes.string,
      city: PropTypes.string,
      state: PropTypes.string,
      post_code: PropTypes.string,
      country: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
      }),
    }),
  ),
  defaultFamilyName: PropTypes.bool,
  accountCard: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }).isRequired,
  defaultImportOpen: PropTypes.bool,
  contentClassName: PropTypes.string,
};

const CardElementAddressControl = ({
  onClose,
  onChange,
  addresses = [],
  defaultFamilyName = false,
  accountCard,
  defaultImportOpen,
  contentClassName,
}) => {
  const { onVirtualPageViewTrack } = useVirtualPageView({
    title: 'Create a card - Choose contacts',
    path: '/element/address',
  });

  const isNativeWebView = useIsNativeWebView();
  const [familyName, setFamilyName] = useState(defaultFamilyName);
  const [selectedAddresses, setSelectedAddresses] = useState(addresses);
  const [editingAddress, setEditingAddress] = useState(null);
  const [loadingAddress, setLoadingAddress] = useState(false);
  const [error, setError] = useState();
  const [externalShippingAddressCreate] = useMutation(
    externalShippingAddressCreateMutation,
  );
  const [externalShippingAddressUpdate] = useMutation(
    externalShippingAddressUpdateMutation,
  );
  const [externalShippingAddressDelete] = useMutation(
    externalShippingAddressDeleteMutation,
  );
  const { data: { me: account } = {} } = useQuery(
    getUserForAccountCardConfigurationQuery,
    {
      fetchPolicy: 'cache-and-network',
    },
  );
  const {
    loading: importingAddresses,
  } = useBulkExternalShippingAddressImport();

  const dispatch = useDispatch();
  const onContactSpreadsheetImportModalOpen = useCallback(
    () => dispatch(openModal(CONTACT_SPREADSHEET_IMPORT_MODAL)),
    [],
  );
  const onOpenAlert = useCallback(state => dispatch(openAlert(state)), [
    dispatch,
  ]);

  useEffect(() => {
    if (defaultImportOpen) {
      onContactSpreadsheetImportModalOpen();
    }
  }, []);

  useEffect(() => {
    if (editingAddress) {
      onVirtualPageViewTrack({
        pageViewTitle: `Create a card - ${
          editingAddress.id ? 'Edit Contact' : 'Add Contact'
        }`,
        pageViewPath: `/element/address/${editingAddress.id ? 'edit' : 'new'}`,
      });
    }
  }, [editingAddress]);

  const externalShippingAddresses =
    account?.external_shipping_addresses?.records ?? [];
  const selectedAddressIds = selectedAddresses.map(({ id }) => id);
  const onToggleAddress = selectedAddress => {
    setSelectedAddresses(prev => {
      if (selectedAddressIds.includes(selectedAddress.id)) {
        return prev.filter(({ id }) => id !== selectedAddress.id);
      }

      return [...prev, selectedAddress];
    });
  };

  const onAddressChange = async userValue => {
    if (loadingAddress) {
      return;
    }

    setLoadingAddress(true);

    try {
      if (userValue.id) {
        await externalShippingAddressUpdate({
          variables: {
            input: {
              external_shipping_address_id: userValue.id,
              country_id: userValue.country?.id,
              ...omit(userValue, ['id', 'country', '__typename']),
            },
          },
          refetchQueries: [
            {
              query: getUserForAccountCardConfigurationQuery,
            },
          ],
        });

        setLoadingAddress(false);
        setEditingAddress(false);
        return;
      }

      const response = await externalShippingAddressCreate({
        variables: {
          input: {
            account_id: account.id,
            ...userValue,
          },
        },
        refetchQueries: [
          {
            query: getUserForAccountCardConfigurationQuery,
          },
        ],
      });

      setLoadingAddress(false);
      setEditingAddress(false);
      onToggleAddress(
        response.data.externalShippingAddressCreate.external_shipping_address,
      );
    } catch (responseError) {
      setError(getErrorString(responseError));
    }
  };

  const onAddressDelete = async userValue => {
    try {
      setError('');

      // If the deleted address is selected for sending remove it.
      if (selectedAddressIds.includes(userValue.id)) {
        setSelectedAddresses(prev =>
          prev.filter(({ id }) => id !== userValue.id),
        );
        await onChange({
          addresses: selectedAddresses.filter(({ id }) => id !== userValue.id),
          familyName,
        });
      }

      await externalShippingAddressDelete({
        variables: {
          input: {
            external_shipping_address_id: userValue.id,
          },
        },
        refetchQueries: [
          {
            query: getUserForAccountCardConfigurationQuery,
          },
        ],
      });
    } catch (responseError) {
      setError(getErrorString(responseError));
    }
  };

  const onAddressMissingInfo = address => {
    onOpenAlert({
      title: 'Incomplete shipping info',
      // eslint-disable-next-line react/prop-types
      message: ({ onRequestClose }) => (
        <Fragment>
          <p className="mt-1">
            This contact has incomplete shipping information, please provide
            them first.
          </p>

          <Button
            btnStyle="link"
            onClick={() => {
              onRequestClose();
              setEditingAddress(address);
            }}
          >
            Edit contact
          </Button>
        </Fragment>
      ),
      type: 'info',
    });
  };

  return (
    <div
      className={classNames({
        'modal-contained-wrap': true,
        [contentClassName]: !!contentClassName,
      })}
    >
      <div className="modal-contained-header">
        <DismissButton onClick={onClose} />

        <span className="modal-contained-title">Shipping addresses</span>

        {!editingAddress && (
          <Button
            btnStyle="link"
            onClick={() => {
              onClose();
              onChange({ familyName, addresses: selectedAddresses });
            }}
          >
            Done
          </Button>
        )}

        {editingAddress && (
          <Button
            btnStyle="link"
            onClick={() => {
              if (externalShippingAddresses.length === 0) {
                onClose();
              } else {
                setEditingAddress(false);
              }
            }}
          >
            Cancel
          </Button>
        )}
      </div>

      {error && <Alert type="danger">{error}</Alert>}

      {externalShippingAddresses.length > 0 && !editingAddress && (
        <div className="modal-contained-content">
          <div className="d-flex px-2 mt-2 align-items-center justify-content-center">
            <span className="text-bold text-light">
              {externalShippingAddresses.length}{' '}
              {pluralize('address', externalShippingAddresses.length)}
            </span>

            <Button
              onClick={() => {
                if (
                  externalShippingAddresses.length === selectedAddresses.length
                ) {
                  setSelectedAddresses([]);
                  return;
                }

                const missingInfoAddress = externalShippingAddresses.find(
                  externalShippingAddress => {
                    if (!canSendToAddress(externalShippingAddress)) {
                      onAddressMissingInfo(externalShippingAddress);

                      return true;
                    }

                    return false;
                  },
                );

                if (missingInfoAddress) {
                  return;
                }

                setSelectedAddresses(externalShippingAddresses);
              }}
              className="ml-auto text-uppercase text-bold"
              btnSize="sm"
              btnStyle="link"
            >
              {externalShippingAddresses.length === selectedAddresses.length
                ? 'Deselect all'
                : 'Select all'}
            </Button>
          </div>
          <div className="address-list-items">
            {externalShippingAddresses.map(externalShippingAddress => {
              return (
                <AddressListItem
                  key={externalShippingAddress.id}
                  address={externalShippingAddress}
                  selectable={canSendToAddress(externalShippingAddress)}
                  selected={selectedAddressIds.includes(
                    externalShippingAddress.id,
                  )}
                  onClick={selectedAddress => {
                    if (!canSendToAddress(externalShippingAddress)) {
                      onAddressMissingInfo(externalShippingAddress);
                    } else {
                      onToggleAddress(selectedAddress);
                    }
                  }}
                  onEdit={selectedAddress => {
                    setEditingAddress(selectedAddress);
                  }}
                  onDelete={selectedAddress => onAddressDelete(selectedAddress)}
                />
              );
            })}
          </div>
        </div>
      )}

      {!editingAddress && (
        <div className="modal-contained-footer">
          <div className="address-list-actions">
            {externalShippingAddresses.length > 0 && (
              <CustomControl
                id="use-family-name"
                type="checkbox"
                label="Address recipients as a family"
                className="address-list-action-button address-list-item-family-button"
                labelNote={
                  familyName ? (
                    <>
                      e.g. &ldquo;The Smith family&rdquo; instead of &ldquo;Joe
                      Smith&rdquo;
                    </>
                  ) : undefined
                }
                checked={familyName}
                onChange={() => setFamilyName(x => !x)}
              />
            )}
            <AddressListItemAddButton onClick={() => setEditingAddress({})} />
            <AddressListItemImportButton
              onImport={() => {
                onContactSpreadsheetImportModalOpen();
              }}
              accountCard={accountCard}
            />
            {isNativeWebView && <AddressListItemContactsImportButton />}
          </div>
        </div>
      )}

      {editingAddress && (
        <div className="modal-contained-content">
          <ExternalShippingAddressForm
            className={classNames({
              'is-loading': loadingAddress,
            })}
            onCancel={() => {
              if (externalShippingAddresses.length === 0) {
                onClose();
              } else {
                setEditingAddress(false);
              }
            }}
            onSubmit={newAddress => {
              onAddressChange(newAddress);
            }}
            address={editingAddress}
            loading={loadingAddress}
          />
        </div>
      )}

      {importingAddresses && (
        <div className="card-element-address-control-loader">
          <NativeActivityIndicator />
          <p>Importing contacts&hellip;</p>
        </div>
      )}
    </div>
  );
};

CardElementAddressControl.propTypes = propTypes;

export const CARD_ELEMENT_ADDRESS_CONTROL_MODAL =
  'CARD_ELEMENT_ADDRESS_CONTROL_MODAL';

export default withModal(CardElementAddressControl, {
  name: CARD_ELEMENT_ADDRESS_CONTROL_MODAL,
  dismissible: false,
  size: 'xs',
  hideHeader: true,
  shouldCloseOnOverlayClick: false,
  className: 'modal-contained',
});
